import { ExternalLink, Paperclip, Sparkles, X } from "lucide-react";
import { useNotify } from "ra-core";

import type { AccExpense, AccFile } from "./accounting";
import { AccountingFileField } from "./AccountingFileField";
import { openAccountingFile } from "./useAccountingData";

const CONFIDENCE: Record<string, string> = {
  high: "alta",
  medium: "media",
  low: "baja",
};

/** What to check before confirming an expense read by the AI or sent by Meta. */
export const ExpenseReviewBanner = ({ expense }: { expense: AccExpense }) => {
  const warnings = expense.ai_data?.warnings ?? [];
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100">
      <div className="flex items-center gap-1.5 font-medium">
        <Sparkles className="h-4 w-4" />
        {expense.source === "meta_ads" && !expense.ai_data
          ? "Gasto leído de Meta Ads: revisa y adjunta los recibos del mes"
          : `Leída por la IA${
              expense.ai_data?.confidence
                ? ` · confianza ${CONFIDENCE[expense.ai_data.confidence] ?? expense.ai_data.confidence}`
                : ""
            }: comprueba los datos con la factura y guarda`}
      </div>
      {warnings.length ? (
        <ul className="list-disc pl-5 text-xs">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

/** More files for the same expense: receipts of the month, delivery notes… */
export const ExtraFilesField = ({
  files,
  day,
  onChange,
}: {
  files: AccFile[];
  day: string;
  onChange: (files: AccFile[]) => void;
}) => {
  const notify = useNotify();
  const open = async (path: string) => {
    try {
      await openAccountingFile(path);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Error", {
        type: "error",
      });
    }
  };
  return (
    <div className="flex flex-col gap-1.5">
      {files.map((file) => (
        <div
          key={file.path}
          className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
        >
          <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
          <button
            type="button"
            onClick={() => open(file.path)}
            className="min-w-0 flex-1 truncate text-left hover:underline"
          >
            {file.name}
          </button>
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <button
            type="button"
            onClick={() => onChange(files.filter((f) => f.path !== file.path))}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Quitar archivo"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <AccountingFileField
        label=""
        value={null}
        folder="expenses"
        day={day}
        uploadLabel="Añadir otro archivo (recibo, albarán…)"
        onChange={(file) => file && onChange([...files, file])}
      />
    </div>
  );
};
